import { Link, type LinkOptions } from '@tanstack/react-router'
import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown } from 'lucide-react'
import { useMemo, useState, type ReactNode } from 'react'
import { cx } from './cx'
import { Skeleton } from './States'

type SortValue = string | number | null | undefined

export type Column<T> = {
  id: string
  header: ReactNode
  cell: (row: T) => ReactNode
  align?: 'start' | 'end' | 'center'
  /** Makes the column sortable (client-side). Nulls sort last in both directions. */
  sortValue?: (row: T) => SortValue
  /** Width utility for the column, e.g. "w-24". */
  width?: string
  /** Role in the phone card list (< md). Columns without it are hidden there. */
  mobile?: 'title' | 'meta' | 'trailing'
  /** Different content for the card list (e.g. "6 Tore" where the header is not visible). */
  mobileCell?: (row: T) => ReactNode
  /** Header text for screen readers when `header` is an icon or empty. */
  srHeader?: string
}

type Sort = { id: string; dir: 'asc' | 'desc' }

type DataTableProps<T> = {
  /** Caption for screen readers ("Kader, 24 Spieler"). */
  label: string
  columns: Column<T>[]
  rows: T[] | undefined
  rowKey: (row: T) => string | number
  /** Each row is a real link — rendered on the first column; the whole row is its hit area. */
  rowLink?: (row: T) => LinkOptions
  /** Alternative to rowLink: a real button on the first column. */
  onRowClick?: (row: T) => void
  /** Accessible name of the row link/button (default: text of the first column). */
  rowLabel?: (row: T) => string
  loading?: boolean
  /** Shown instead of rows when there are none (an EmptyState). */
  empty?: ReactNode
  defaultSort?: Sort
  /** Highlight a row (own club in a league table) with the accent marker. */
  highlight?: (row: T) => boolean
  skeletonRows?: number
  /** Leading visual of each phone card (avatar, club logo). */
  mobileLeading?: (row: T) => ReactNode
}

const alignClass = { start: 'text-left', end: 'text-right', center: 'text-center' }
const justifyClass = { start: 'justify-start', end: 'justify-end', center: 'justify-center' }

function compare(a: SortValue, b: SortValue) {
  if (a === b) return 0
  if (a === null || a === undefined) return 1
  if (b === null || b === undefined) return -1
  return typeof a === 'number' && typeof b === 'number' ? a - b : String(a).localeCompare(String(b), 'de', { numeric: true })
}

/**
 * Data table: sortable headers (buttons with aria-sort), sticky header, real link/button rows, skeleton
 * rows in final size, empty slot. Under md it becomes a card list built from the `mobile` columns.
 */
export function DataTable<T>({
  label,
  columns,
  rows,
  rowKey,
  rowLink,
  onRowClick,
  rowLabel,
  loading,
  empty,
  defaultSort,
  highlight,
  skeletonRows = 6,
  mobileLeading,
}: DataTableProps<T>) {
  const [sort, setSort] = useState<Sort | undefined>(defaultSort)

  const sorted = useMemo(() => {
    if (!rows) return []
    const col = sort && columns.find((c) => c.id === sort.id)
    if (!sort || !col?.sortValue) return rows
    const get = col.sortValue
    const factor = sort.dir === 'asc' ? 1 : -1
    return rows.toSorted((a, b) => {
      const va = get(a)
      const vb = get(b)
      // Nulls last regardless of direction.
      if (va === null || va === undefined) return vb === null || vb === undefined ? 0 : 1
      if (vb === null || vb === undefined) return -1
      return compare(va, vb) * factor
    })
  }, [rows, sort, columns])

  function toggle(col: Column<T>) {
    setSort((s) =>
      s?.id !== col.id
        ? // Numbers read best high → low first (goals, minutes); text A → Z.
          { id: col.id, dir: rows?.[0] !== undefined && typeof col.sortValue?.(rows[0]) === 'number' ? 'desc' : 'asc' }
        : { id: col.id, dir: s.dir === 'asc' ? 'desc' : 'asc' },
    )
  }

  const interactive = Boolean(rowLink || onRowClick)
  const isLoading = loading || rows === undefined
  const isEmpty = !isLoading && sorted.length === 0

  function primary(row: T, children: ReactNode, className: string) {
    const name = rowLabel?.(row)
    // after:inset-0 stretches the one real link over the whole row: one tab stop, whole-row hit area.
    const stretch = 'after:absolute after:inset-0 after:content-[""] focus-visible:outline-none'
    if (rowLink)
      return (
        <Link {...rowLink(row)} aria-label={name} className={cx(className, stretch)}>
          {children}
        </Link>
      )
    if (onRowClick)
      return (
        <button type="button" onClick={() => onRowClick(row)} aria-label={name} className={cx(className, stretch, 'text-left')}>
          {children}
        </button>
      )
    return children
  }

  const mobileTitle = columns.find((c) => c.mobile === 'title') ?? columns[0]
  const mcell = (c: Column<T>, row: T) => (c.mobileCell ?? c.cell)(row)
  const mobileMeta = columns.filter((c) => c.mobile === 'meta')
  const mobileTrailing = columns.filter((c) => c.mobile === 'trailing')

  return (
    <div aria-busy={isLoading || undefined}>
      {/* ≥ md: table. Horizontal scroll only up to lg; from lg the wrapper is not a scroll container, so the
          header can stick to the page (a scroll container would capture `position: sticky`). */}
      <div className="hidden overflow-x-auto md:block lg:overflow-visible">
        <table className="w-full border-separate border-spacing-0 text-small">
          <caption className="sr-only">{label}</caption>
          <thead>
            <tr>
              {columns.map((c) => {
                const active = sort?.id === c.id
                const align = c.align ?? 'start'
                return (
                  <th
                    key={c.id}
                    scope="col"
                    aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                    className={cx(
                      'sticky top-[var(--sticky-top,0px)] z-10 h-10 border-b border-line bg-bg px-3 text-label font-medium whitespace-nowrap text-ink-3 first:pl-0 last:pr-0',
                      alignClass[align],
                      c.width,
                    )}
                  >
                    {c.srHeader && !c.sortValue && <span className="sr-only">{c.srHeader}</span>}
                    {c.sortValue ? (
                      <button
                        type="button"
                        onClick={() => toggle(c)}
                        aria-label={c.srHeader}
                        className={cx(
                          'group/sort -mx-1 inline-flex h-7 items-center gap-1 rounded-[6px] px-1 transition-colors hover:text-ink',
                          active && 'text-ink',
                          justifyClass[align],
                          align === 'end' && 'flex-row-reverse',
                        )}
                      >
                        <span aria-hidden={c.srHeader ? true : undefined}>{c.header}</span>
                        {active ? (
                          sort.dir === 'asc' ? (
                            <ArrowUp aria-hidden="true" className="size-3.5" />
                          ) : (
                            <ArrowDown aria-hidden="true" className="size-3.5" />
                          )
                        ) : (
                          <ChevronsUpDown aria-hidden="true" className="size-3.5 opacity-0 transition-opacity group-hover/sort:opacity-60 group-focus-visible/sort:opacity-60" />
                        )}
                      </button>
                    ) : (
                      <span aria-hidden={c.srHeader ? true : undefined}>{c.header}</span>
                    )}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: skeletonRows }, (_, i) => (
                <tr key={i} aria-hidden="true">
                  {columns.map((c, ci) => (
                    <td key={c.id} className={cx('h-12 border-b border-line px-3 first:pl-0 last:pr-0', alignClass[c.align ?? 'start'])}>
                      <Skeleton variant="text" className={cx('inline-block', ci === 0 ? 'w-36' : 'w-10')} />
                    </td>
                  ))}
                </tr>
              ))}
            {isEmpty && (
              <tr>
                <td colSpan={columns.length} className="border-b border-line">
                  {empty ?? <p className="py-10 text-center text-small text-ink-3">Keine Einträge.</p>}
                </td>
              </tr>
            )}
            {!isLoading &&
              sorted.map((row) => {
                const hl = highlight?.(row)
                return (
                  <tr
                    key={rowKey(row)}
                    className={cx(
                      'group/row relative',
                      interactive && 'cursor-pointer has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-accent',
                    )}
                  >
                    {columns.map((c, ci) => (
                      <td
                        key={c.id}
                        className={cx(
                          'h-12 border-b border-line px-3 text-ink tabular first:pl-0 last:pr-0',
                          interactive && !hl && 'transition-colors group-hover/row:bg-bg-subtle',
                          hl && 'bg-accent-soft',
                          // Accent bar marks the highlighted row without colouring its text.
                          hl && ci === 0 && 'shadow-[inset_2px_0_0_var(--accent)] pl-3!',
                          alignClass[c.align ?? 'start'],
                          c.width,
                        )}
                      >
                        {ci === 0 && interactive ? primary(row, c.cell(row), 'rounded-[4px]') : c.cell(row)}
                      </td>
                    ))}
                  </tr>
                )
              })}
          </tbody>
        </table>
      </div>

      {/* < md: card list from the mobile columns. */}
      <ul aria-label={label} className="divide-y divide-line border-y border-line md:hidden">
        {isLoading &&
          Array.from({ length: Math.min(skeletonRows, 4) }, (_, i) => (
            <li key={i} aria-hidden="true" className="flex h-16 flex-col justify-center gap-2">
              <Skeleton variant="text" className="w-40" />
              <Skeleton variant="text" className="w-24" />
            </li>
          ))}
        {isEmpty && <li>{empty ?? <p className="py-10 text-center text-small text-ink-3">Keine Einträge.</p>}</li>}
        {!isLoading &&
          sorted.map((row) => (
            <li
              key={rowKey(row)}
              className={cx(
                'relative flex min-h-16 items-center gap-3 py-3',
                highlight?.(row) && '-mx-4 bg-accent-soft px-4 shadow-[inset_2px_0_0_var(--accent)]',
                interactive && 'active:bg-bg-subtle has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-accent',
              )}
            >
              {mobileLeading && <div className="flex shrink-0">{mobileLeading(row)}</div>}
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="min-w-0 truncate text-body text-ink">
                  {mobileTitle && (interactive ? primary(row, mcell(mobileTitle, row), '') : mcell(mobileTitle, row))}
                </div>
                {mobileMeta.length > 0 && (
                  <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-small text-ink-3">
                    {mobileMeta.map((c, i) => (
                      <span key={c.id} className="inline-flex items-center gap-1.5">
                        {i > 0 && <span aria-hidden="true">·</span>}
                        {mcell(c, row)}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {mobileTrailing.length > 0 && (
                <div className="flex shrink-0 items-center gap-2 text-small text-ink tabular">
                  {mobileTrailing.map((c) => (
                    <span key={c.id}>{mcell(c, row)}</span>
                  ))}
                </div>
              )}
              {interactive && <ChevronRight aria-hidden="true" className="size-4 shrink-0 text-ink-3" />}
            </li>
          ))}
      </ul>
    </div>
  )
}
